import { useEffect, useRef, useState } from "react";
import { authService, type StatusResponse } from "@/services/AuthService";
import { telegramActivationFailure, type TelegramActivationPhase } from "@/utils/telegramActivation";
import { subscribeAuthSession } from "@/lib/auth-session";

export function useTelegramActivation(chatId: string | null, onSuccess: (response: StatusResponse) => void) {
  const [state, setState] = useState<{ phase: TelegramActivationPhase; message: string; saleUnavailable: boolean; actionKey?: string }>({ phase: "waiting", message: "", saleUnavailable: false });
  const [attempt, setAttempt] = useState(0);
  const busy = useRef(false);
  const version = useRef(0);
  const success = useRef(onSuccess);
  success.current = onSuccess;

  useEffect(() => {
    if (state.phase !== "authenticated") return;
    return subscribeAuthSession(() => {
      if (localStorage.getItem("authToken") || localStorage.getItem("sellSupportAuthToken")) return;
      version.current++;
      busy.current = false;
      setState({ phase: chatId ? "checking" : "waiting", message: "", saleUnavailable: false });
      if (chatId) setAttempt(previous => previous + 1);
    });
  }, [chatId, state.phase]);

  useEffect(() => {
    const current = ++version.current;
    busy.current = Boolean(chatId);
    setState({ phase: chatId ? "checking" : "waiting", message: "", saleUnavailable: false });
    if (chatId) {
      void Promise.resolve().then(() => authService.verifyActivation({ telegramChatId: Number(chatId) }))
        .then((response) => {
          if (current !== version.current) return;
          setState({ phase: "authenticated", message: "", saleUnavailable: false });
          success.current(response);
        }).catch((failure) => {
          if (current === version.current) setState(telegramActivationFailure(failure));
        }).finally(() => { if (current === version.current) busy.current = false; });
    }
    return () => { version.current++; busy.current = false; };
  }, [chatId, attempt]);

  const activate = async (activationCode: string) => {
    if (busy.current || !chatId || !activationCode.trim()) return;
    busy.current = true;
    const current = version.current;
    setState({ phase: "activating", message: "", saleUnavailable: false });
    try {
      const response = await authService.activate({ activationCode: activationCode.trim(), telegramChatId: Number(chatId) });
      if (current !== version.current) return;
      setState({ phase: "authenticated", message: "", saleUnavailable: false });
      success.current(response);
    } catch (failure) {
      if (current === version.current) setState(telegramActivationFailure(failure, true));
    } finally {
      if (current === version.current) busy.current = false;
    }
  };

  return { ...state, activate, retry: () => { if (!busy.current) setAttempt((previous) => previous + 1); } };
}
